import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import DOMPurify from 'dompurify';
import { Button, Tooltip, Divider } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import ErrorBoundary from '~/components/common/ErrorBoundary';

export interface MentionUserItem {
  id: number;
  fullname: string;
  username?: string;
  email?: string;
  roleLabel?: string;
}

export interface EditorPropsType {
  placeholder?: string;
  content: string;
  style?: CSSProperties;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  handleChangeContent: (newValue: string, mentionedUserIds?: number[]) => void;
  spellCheck?: boolean;
  minHeight?: number;
  compact?: boolean;
  mentionUsers?: MentionUserItem[];
  onSubmitShortcut?: () => void;
  onCancelShortcut?: () => void;
}

/**
 * Extracts unique numeric user IDs from Tiptap mention spans (`data-id="..."`) in an HTML string.
 */
export const extractMentionedUserIdsFromHtml = (html?: string): number[] => {
  if (!html || typeof html !== 'string') return [];
  const ids = new Set<number>();
  const regex = /<[^>]*data-id=["'](\d+)["'][^>]*>/gi;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const id = Number(match[1]);
    if (Number.isFinite(id) && id > 0) {
      ids.add(id);
    }
  }
  return Array.from(ids);
};

/**
 * Converts plain text or basic Markdown into safe HTML while preserving full Tiptap HTML and mention tags.
 */
export const sanitizeAndFormatNoteHtml = (raw?: string): string => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const hasHtmlTags = /<\/?(p|br|strong|b|em|i|u|s|ul|ol|li|h[1-6]|blockquote|pre|code|span|div|a)\b[^>]*>/i.test(
    trimmed,
  );

  let htmlToSanitize = trimmed;
  if (!hasHtmlTags) {
    const escaped = trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    htmlToSanitize = escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(
        /@([A-Za-zÀ-ỹ0-9_]+(?:\s+[A-Za-zÀ-ỹ0-9_]+){0,3})/g,
        '<span class="mention" data-type="mention">@$1</span>',
      )
      .replace(/\n/g, '<br/>');
  }

  return DOMPurify.sanitize(htmlToSanitize, {
    ADD_ATTR: ['data-type', 'data-id', 'data-label', 'class', 'target', 'rel'],
  });
};

export interface RichTextViewerProps {
  content?: string;
  className?: string;
  style?: CSSProperties;
  strikethrough?: boolean;
}

export const RichTextViewer: React.FC<RichTextViewerProps> = ({
  content,
  className,
  style,
  strikethrough = false,
}) => {
  const sanitizedHtml = useMemo(() => sanitizeAndFormatNoteHtml(content), [content]);

  if (!sanitizedHtml) {
    return null;
  }

  return (
    <>
      <div
        className={`geocloud-richtext-viewer ${className || ''}`}
        style={{
          textDecoration: strikethrough ? 'line-through' : 'none',
          ...style,
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
      <style>{`
        .geocloud-richtext-viewer {
          font-size: 12.5px;
          line-height: 1.5;
          color: #1e293b;
          word-break: break-word;
        }
        .geocloud-richtext-viewer p {
          margin: 0 0 4px 0;
        }
        .geocloud-richtext-viewer p:last-child {
          margin-bottom: 0;
        }
        .geocloud-richtext-viewer ul,
        .geocloud-richtext-viewer ol {
          margin: 4px 0;
          padding-left: 18px;
        }
        .geocloud-richtext-viewer code {
          background: #f1f5f9;
          color: #dc2626;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 12px;
          font-family: monospace;
        }
        .geocloud-richtext-viewer blockquote {
          border-left: 3px solid #3b82f6;
          margin: 4px 0;
          padding: 2px 8px;
          background: #eff6ff;
          color: #334155;
          border-radius: 0 4px 4px 0;
        }
        .geocloud-richtext-viewer .mention,
        .geocloud-richtext-viewer span[data-type="mention"] {
          background-color: #dbeafe;
          color: #1d4ed8;
          font-weight: 600;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 12px;
          display: inline-block;
          line-height: 1.35;
        }
      `}</style>
    </>
  );
};

const RichTextEditor: React.FC<EditorPropsType> = ({
  placeholder = 'Nhập nội dung ghi chú... (gõ @ để nhắc tên, hỗ trợ **in đậm**)',
  content,
  style,
  className,
  autoFocus = false,
  disabled = false,
  handleChangeContent,
  spellCheck = false,
  minHeight = 84,
  compact = false,
  mentionUsers = [],
  onSubmitShortcut,
  onCancelShortcut,
}) => {
  const isUpdatingRef = useRef(false);
  const mentionUsersRef = useRef<MentionUserItem[]>(mentionUsers);
  mentionUsersRef.current = mentionUsers;

  const wrapperRef = useRef<HTMLDivElement>(null);

  // State for @mention floating suggestion dropdown (triggered by typing '@' or clicking toolbar '@' button)
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const suggestionCommandRef = useRef<((attrs: { id: string; label: string }) => void) | null>(null);

  const filteredMentionUsers = useMemo(() => {
    const q = mentionQuery.trim().toLowerCase();
    const list = mentionUsers;
    if (!q) return list.slice(0, 8);
    return list
      .filter(
        (u) =>
          (u.fullname || '').toLowerCase().includes(q) ||
          (u.username || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [mentionUsers, mentionQuery]);

  const updateDropdownPosition = useCallback((clientRect?: (() => DOMRect | null) | null) => {
    if (clientRect) {
      const rect = clientRect();
      if (rect) {
        setDropdownPos({
          top: Math.min(window.innerHeight - 220, rect.bottom + 6),
          left: Math.min(window.innerWidth - 250, Math.max(8, rect.left)),
        });
        return;
      }
    }
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPos({
        top: Math.min(window.innerHeight - 220, rect.top + 38),
        left: Math.min(window.innerWidth - 250, Math.max(8, rect.left + 12)),
      });
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        renderLabel({ node }) {
          return `@${node.attrs.label ?? node.attrs.id}`;
        },
        suggestion: {
          char: '@',
          allowSpaces: true,
          items: ({ query }) => {
            const q = (query || '').trim().toLowerCase();
            return mentionUsersRef.current
              .filter(
                (u) =>
                  !q ||
                  (u.fullname || '').toLowerCase().includes(q) ||
                  (u.username || '').toLowerCase().includes(q),
              )
              .slice(0, 8);
          },
          render: () => {
            return {
              onStart: (props) => {
                suggestionCommandRef.current = (attrs) => props.command(attrs);
                setMentionQuery(props.query || '');
                setSelectedIndex(0);
                updateDropdownPosition(props.clientRect);
                setMentionOpen(true);
              },
              onUpdate: (props) => {
                suggestionCommandRef.current = (attrs) => props.command(attrs);
                setMentionQuery(props.query || '');
                setSelectedIndex(0);
                updateDropdownPosition(props.clientRect);
              },
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  setMentionOpen(false);
                  return true;
                }
                return false;
              },
              onExit: () => {
                suggestionCommandRef.current = null;
                setMentionOpen(false);
              },
            };
          },
        },
      }),
    ],
    content: sanitizeAndFormatNoteHtml(content) || '',
    editable: !disabled,
    autofocus: autoFocus,
    onUpdate: ({ editor: ed }) => {
      isUpdatingRef.current = true;
      const html = ed.getHTML();
      const mentionedIds = extractMentionedUserIdsFromHtml(html);
      handleChangeContent(html, mentionedIds);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    },
  });

  useEffect(() => {
    if (!editor || isUpdatingRef.current) return;
    const currentHtml = editor.getHTML();
    const normalizedIncoming = sanitizeAndFormatNoteHtml(content);
    if (normalizedIncoming !== currentHtml && content !== currentHtml) {
      editor.commands.setContent(normalizedIncoming || '');
    }
  }, [content, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const handleSelectMentionUser = useCallback(
    (user: MentionUserItem) => {
      if (!editor) return;
      const label = user.fullname || user.username || `User #${user.id}`;
      if (suggestionCommandRef.current) {
        suggestionCommandRef.current({ id: String(user.id), label });
      } else {
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: 'mention',
              attrs: {
                id: String(user.id),
                label,
              },
            },
            {
              type: 'text',
              text: ' ',
            },
          ])
          .run();
      }
      setMentionOpen(false);
      setMentionQuery('');
    },
    [editor],
  );

  const handleToolbarMentionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!editor) return;
    suggestionCommandRef.current = null;
    setMentionQuery('');
    setSelectedIndex(0);
    updateDropdownPosition(null);
    setMentionOpen((prev) => !prev);
  };

  const handleKeyDownCapture = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;

    if (mentionOpen && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        return;
      }
      if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        e.stopPropagation();
        const chosen = filteredMentionUsers[selectedIndex] || filteredMentionUsers[0];
        if (chosen) {
          handleSelectMentionUser(chosen);
        }
        return;
      }
    }

    if ((e.ctrlKey || e.metaKey || e.altKey) && (e.key === 'Enter' || e.code === 'Enter')) {
      if (onSubmitShortcut) {
        e.preventDefault();
        e.stopPropagation();
        onSubmitShortcut();
      }
      return;
    }

    if (e.key === 'Escape' && !mentionOpen && onCancelShortcut) {
      e.preventDefault();
      e.stopPropagation();
      onCancelShortcut();
    }
  };

  if (!editor) return null;

  return (
    <div
      ref={wrapperRef}
      style={{
        border: disabled ? 'none' : '1px solid #cbd5e1',
        borderRadius: 6,
        background: '#ffffff',
        overflow: 'hidden',
        ...style,
      }}
      className={`geocloud-tiptap-editor ${className || ''}`}
      spellCheck={spellCheck}
      data-testid="tiptap-note-editor"
      onKeyDownCapture={handleKeyDownCapture}
    >
      <ErrorBoundary compact title="Lỗi trình soạn thảo">
        {!disabled && (
          <div
            style={{
              padding: compact ? '3px 6px' : '5px 8px',
              borderBottom: '1px solid #f1f5f9',
              background: '#f8fafc',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
            data-testid="tiptap-toolbar"
          >
            <Tooltip title="In đậm (Ctrl+B hoặc **chữ**)">
              <Button
                size="small"
                type={editor.isActive('bold') ? 'primary' : 'text'}
                icon={<BoldOutlined />}
                data-testid="tiptap-bold-btn"
                onClick={() => editor.chain().focus().toggleBold().run()}
              />
            </Tooltip>
            <Tooltip title="In nghiêng (Ctrl+I hoặc *chữ*)">
              <Button
                size="small"
                type={editor.isActive('italic') ? 'primary' : 'text'}
                icon={<ItalicOutlined />}
                data-testid="tiptap-italic-btn"
                onClick={() => editor.chain().focus().toggleItalic().run()}
              />
            </Tooltip>
            <Tooltip title="Danh sách gạch đầu dòng (- )">
              <Button
                size="small"
                type={editor.isActive('bulletList') ? 'primary' : 'text'}
                icon={<UnorderedListOutlined />}
                data-testid="tiptap-bullet-list-btn"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              />
            </Tooltip>
            <Tooltip title="Danh sách đánh số (1. )">
              <Button
                size="small"
                type={editor.isActive('orderedList') ? 'primary' : 'text'}
                icon={<OrderedListOutlined />}
                data-testid="tiptap-ordered-list-btn"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              />
            </Tooltip>
            <Tooltip title="Mã / Tọa độ (`code`)">
              <Button
                size="small"
                type={editor.isActive('code') ? 'primary' : 'text'}
                icon={<CodeOutlined />}
                data-testid="tiptap-code-btn"
                onClick={() => editor.chain().focus().toggleCode().run()}
              />
            </Tooltip>

            <Divider type="vertical" style={{ margin: '0 2px' }} />

            <Tooltip title="Nhắc tên thành viên dự án (@mention)">
              <Button
                size="small"
                type={mentionOpen ? 'primary' : 'text'}
                data-testid="tiptap-mention-btn"
                onClick={handleToolbarMentionClick}
                style={{ fontWeight: 600, fontSize: 12, padding: '0 6px', color: mentionOpen ? '#fff' : '#2563eb' }}
              >
                @ Nhắc tên
              </Button>
            </Tooltip>
          </div>
        )}

        <div
          style={{
            padding: disabled ? '0' : compact ? '6px 8px' : '8px 10px',
            minHeight,
            cursor: disabled ? 'default' : 'text',
          }}
          onClick={() => {
            if (!disabled && !editor.isFocused) {
              editor.commands.focus();
            }
          }}
        >
          <EditorContent editor={editor} className="geocloud-tiptap-content" />
        </div>

        {mentionOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              data-testid="tiptap-mention-dropdown"
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: 240,
                maxHeight: 210,
                overflowY: 'auto',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
                zIndex: 10050,
                padding: 4,
              }}
              onMouseDown={(e) => {
                // Keep editor focus while selecting a member
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#64748b',
                  padding: '4px 8px',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                Nhắc tên thành viên dự án
              </div>
              {filteredMentionUsers.length === 0 ? (
                <div style={{ padding: '8px 10px', fontSize: 12, color: '#94a3b8' }}>
                  Không tìm thấy thành viên phù hợp
                </div>
              ) : (
                filteredMentionUsers.map((user, idx) => (
                  <div
                    key={user.id}
                    data-testid={`tiptap-mention-option-${user.id}`}
                    onClick={() => handleSelectMentionUser(user)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: idx === selectedIndex ? '#eff6ff' : 'transparent',
                      color: '#1e293b',
                      fontSize: 12.5,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: '#1d4ed8' }}>
                        @{user.fullname || user.username}
                      </span>
                      {user.username && (
                        <span style={{ fontSize: 11, color: '#64748b' }}>{user.username}</span>
                      )}
                    </div>
                    {user.roleLabel && (
                      <span
                        style={{
                          fontSize: 10,
                          background: '#f1f5f9',
                          color: '#475569',
                          padding: '1px 5px',
                          borderRadius: 4,
                        }}
                      >
                        {user.roleLabel}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>,
            document.body,
          )}

        <style>{`
          .geocloud-tiptap-content .tiptap {
            outline: none;
            min-height: ${typeof minHeight === 'number' ? `${minHeight}px` : minHeight};
            font-size: 13px;
            line-height: 1.5;
            color: #1e293b;
          }
          .geocloud-tiptap-content .tiptap p.is-editor-empty:first-child::before {
            color: #94a3b8;
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
          .geocloud-tiptap-content .tiptap p {
            margin: 0 0 6px 0;
          }
          .geocloud-tiptap-content .tiptap p:last-child {
            margin-bottom: 0;
          }
          .geocloud-tiptap-content .tiptap ul,
          .geocloud-tiptap-content .tiptap ol {
            padding-left: 18px;
            margin: 4px 0;
          }
          .geocloud-tiptap-content .tiptap code {
            background: #f1f5f9;
            color: #dc2626;
            padding: 1px 5px;
            border-radius: 4px;
            font-size: 12px;
            font-family: monospace;
          }
          .geocloud-tiptap-content .tiptap .mention,
          .geocloud-tiptap-content .tiptap span[data-type="mention"] {
            background-color: #dbeafe;
            color: #1d4ed8;
            font-weight: 600;
            padding: 1px 6px;
            border-radius: 4px;
            font-size: 12px;
          }
        `}</style>
      </ErrorBoundary>
    </div>
  );
};

export default RichTextEditor;
